/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import {
  TaxCardLine,
  fetchTaxCard,
  getCurrentYear,
  getYearOptions,
} from "./displayTaxCard.service";

const DisplayTaxCard: React.FC = () => {
  const yearOptions = useMemo(() => getYearOptions(10), []);
  const currentYear = useMemo(() => getCurrentYear(), []);

  const [year, setYear] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [taxCardData, setTaxCardData] = useState<TaxCardLine[]>([]);
  const [touched, setTouched] = useState(false);

  const yearError = !year
    ? "Year is required"
    : year === currentYear
    ? "Please do not select current Financial Year"
    : "";

  const handleYearChange = (value: string) => {
    setYear(value);
    setTaxCardData([]);
    if (value === currentYear) {
      setError("Please do not select current Financial Year");
    } else {
      setError("");
    }
  };

  const handleClear = () => {
    setYear("");
    setTaxCardData([]);
    setError("");
    setTouched(false);
  };

  const handleGetTaxCard = async () => {
    setTouched(true);
    if (!year) {
      setError("Year is required");
      return;
    }
    if (year === currentYear) {
      setError("Please do not select current Financial Year");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const tableLines = await fetchTaxCard(year);
      setTaxCardData(tableLines);
      if (tableLines.length === 0) {
        setError("No tax card data found for the selected year");
      }
    } catch (err: any) {
      setTaxCardData([]);
      const message =
        err?.message || "An error occurred while fetching tax card data";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const headerItem = taxCardData[0];

  return (
    <div className="page-wrapper">
      <style>{taxCardStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Application to get Taxcard</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Tax & Loan</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Display Tax Card
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="card tax-filter-card mb-3">
          <div className="card-body">
            <div className="tax-info-box mb-3">
              <h6 className="mb-1 fw-bold">Tax Card Information</h6>
              <p className="mb-0 text-muted fs-13">
                Select a year to view your tax card details
              </p>
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-sm-6 col-md-4 col-lg-3">
                <label className="form-label fw-medium mb-1">
                  <i className="ti ti-calendar me-1 text-primary" />
                  Enter Year
                </label>
                <select
                  className={`form-select ${
                    touched && yearError ? "is-invalid" : ""
                  }`}
                  value={year}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  <option value="">Select year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                {touched && yearError && (
                  <div className="invalid-feedback d-block">{yearError}</div>
                )}
              </div>
              <div className="col-sm-6 col-md-8 col-lg-9 d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                  onClick={handleGetTaxCard}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <i className="ti ti-file-text" />
                  )}
                  Get Taxcard
                </button>
              </div>
            </div>

            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3 mb-0">Loading tax card data</p>
              </div>
            )}

            {error && !loading && (
              <div className="tax-error-box mt-3">{error}</div>
            )}
          </div>
        </div>

        {!loading && taxCardData.length > 0 && (
          <div className="card tax-card-result">
            {(headerItem?.EmpID || headerItem?.Year) && (
              <div className="tax-card-header">
                <div className="d-flex flex-wrap gap-2">
                  {headerItem?.EmpID && (
                    <span className="tax-header-badge">ID: {headerItem.EmpID}</span>
                  )}
                  {headerItem?.Year && (
                    <span className="tax-header-badge">Year: {headerItem.Year}</span>
                  )}
                </div>
              </div>
            )}
            <div className="tax-card-body">
              <pre className="tax-line-pre">
                {taxCardData
                  .map((item) => item.Tableline ?? "")
                  .join("\n")}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const taxCardStyles = `
.tax-filter-card { border: none; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.tax-info-box {
  background: #F4F8FF;
  border-radius: 12px;
  padding: 14px 16px;
  border-left: 4px solid var(--primary, #dc2626);
}
.fs-13 { font-size: 13px; }
.tax-error-box {
  padding: 14px 16px;
  background: #FFEBEE;
  border-radius: 12px;
  border: 1px solid #FFCDD2;
  color: #D32F2F;
  text-align: center;
  font-weight: 500;
}
.tax-card-result {
  border: 1px solid #eee;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0,0,0,0.08);
}
.tax-card-header {
  padding: 12px 16px;
  background: #667eea;
}
.tax-header-badge {
  background: rgba(255,255,255,0.95);
  padding: 6px 12px;
  border-radius: 24px;
  font-weight: 700;
  color: #667eea;
  font-size: 12px;
}
.tax-card-body {
  background: #fff;
  overflow: auto;
  max-height: 70vh;
}
.tax-line-pre {
  font-family: "Courier New", Courier, monospace;
  font-size: 12px;
  line-height: 18px;
  color: #333;
  white-space: pre;
  margin: 0;
  padding: 16px;
}
`;

export default DisplayTaxCard;
